import { createSelector, createSlice } from "@reduxjs/toolkit";
import IUser from "./IUser";
import {
  fetchUserInformation,
  fetchUserProfile,
  loginUserWithPassword,
  logoutAllSessions,
  logoutUser,
  refreshUserInformation,
  updateUserData,
  uploadUserProfilePhoto,
} from "./auth.thunk";
import IUserProfile from "./IUserProfile";

/**
 * Boot/auth state machine.
 *
 *   - `idle`           — Page hasn't fired `/me` yet (cold boot).
 *   - `bootstrapping`  — `/me` is in flight or login is pending.
 *   - `ready`          — boot resolved; check `isAuthenticated` for
 *                        the actual sign-in state.
 *
 * Replaces the pre-Phase-5 `initialFetch: boolean | null` 3-state
 * union, which conflated "loading state" and "boot lifecycle". The
 * runtime semantics are unchanged:
 *
 *   - old `null` → new `'idle'`
 *   - old `false` → new `'bootstrapping'`
 *   - old `true`  → new `'ready'`
 */
export type AuthBootStatus = "idle" | "bootstrapping" | "ready";

export interface IAuthState {
  bootStatus: AuthBootStatus;
  isAuthenticated: boolean;
  user: IUser | null;
  profileData: IUserProfile | null;
  loadingAuth: boolean;
  loadingProfile: boolean;
}

const initialState: IAuthState = {
  bootStatus: "idle",
  isAuthenticated: false,
  user: null,
  profileData: null,
  loadingAuth: false,
  loadingProfile: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /** Drop the boot state back to idle (used after destructive logouts). */
    resetBootStatus(state) {
      state.bootStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInformation.pending, (state) => {
        // Only transition `idle → bootstrapping` for the cold-boot
        // /me call. Subsequent refreshes (after creating a ticket,
        // editing the profile, etc.) must NOT reset bootStatus —
        // doing so causes Page.tsx to re-render the <Loader />
        // shell, unmounting the BrowserRouter + ToastContainer
        // and destroying any in-flight toast. The full-page flicker
        // bug after creating a bet ticket was this.
        if (state.bootStatus === "idle") {
          state.bootStatus = "bootstrapping";
        }
        state.loadingAuth = true;
      })
      .addCase(fetchUserInformation.fulfilled, (state, { payload }) => {
        state.bootStatus = "ready";
        state.isAuthenticated = true;
        state.user = payload.data;
        state.loadingAuth = false;
      })
      .addCase(fetchUserInformation.rejected, (state) => {
        state.bootStatus = "ready";
        state.isAuthenticated = false;
        state.user = null;
        state.loadingAuth = false;
      });
    builder
      .addCase(loginUserWithPassword.pending, (state) => {
        state.bootStatus = "bootstrapping";
        state.loadingAuth = true;
      })
      .addCase(loginUserWithPassword.fulfilled, (state, { payload }) => {
        state.bootStatus = "ready";
        state.isAuthenticated = true;
        state.user = payload.data.user;
        state.profileData = null;
        state.loadingAuth = false;
        // Push registration moved to the thunk's success path — see
        // `loginUserWithPassword` in `auth.thunk.ts`. Reducers must
        // stay pure under RTK + Immer.
      })
      .addCase(loginUserWithPassword.rejected, (state) => {
        state.bootStatus = "ready";
        state.isAuthenticated = false;
        state.user = null;
        state.loadingAuth = false;
      });

    // NOTE: `refreshUserInformation.rejected` must NOT de-auth.
    // De-authentication has exactly two writers: the logout actions
    // (`onLogout` below) and the boot /me rejection
    // (`fetchUserInformation.rejected`). The axios interceptor's
    // `onUnauthorized` callback (wired in index.tsx) runs AFTER a
    // failed refresh and reads `auth.isAuthenticated` to decide
    // whether a full logout teardown (server /auth/logout, push
    // unsubscribe, query-cache wipe) is needed — if this reducer
    // flipped the flag first, that check could never see the live
    // session and the teardown would be unreachable.
    builder
      .addCase(refreshUserInformation.pending, (state) => {
        state.loadingAuth = true;
      })
      .addCase(refreshUserInformation.fulfilled, (state) => {
        state.loadingAuth = false;
      })
      .addCase(refreshUserInformation.rejected, (state) => {
        state.loadingAuth = false;
      });

    builder
      .addCase(updateUserData.pending, (state) => {
        state.loadingAuth = true;
      })
      .addCase(updateUserData.fulfilled, (state, { payload }) => {
        state.user = payload.data.user;
        state.loadingAuth = false;
      })
      .addCase(updateUserData.rejected, (state) => {
        state.loadingAuth = false;
      });

    builder.addCase(uploadUserProfilePhoto.fulfilled, (state, { payload }) => {
      if (payload.data.photoPath && state.user) {
        state.user.photoUrl = payload.data.photoPath;
      }
    });

    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loadingProfile = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, { payload }) => {
        state.profileData = payload.data;
        state.loadingProfile = false;
      })
      .addCase(fetchUserProfile.rejected, (state) => {
        state.profileData = null;
        state.loadingProfile = false;
      });

    // logout + logout-all behave identically from the UI's perspective.
    const onLogout = (state: IAuthState) => {
      state.bootStatus = "ready";
      state.isAuthenticated = false;
      state.user = null;
      state.profileData = null;
    };
    builder
      .addCase(logoutUser.fulfilled, onLogout)
      .addCase(logoutUser.rejected, onLogout)
      .addCase(logoutAllSessions.fulfilled, onLogout)
      .addCase(logoutAllSessions.rejected, onLogout);
  },
});

export const { resetBootStatus } = authSlice.actions;
export default authSlice.reducer;

export const userSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.user,
);
export const isAuthenticatedSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.isAuthenticated,
);
/** Current boot lifecycle state. Consumers should compare against `"ready"`. */
export const bootStatusSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.bootStatus,
);
export const loadingAuthSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.loadingAuth,
);

export const profileDataSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.profileData,
);

export const loadingProfileSelector = createSelector(
  (state: { auth: IAuthState }) => state.auth,
  (auth) => auth.loadingProfile,
);
